import { useState, useEffect, useRef } from 'react';
import { User, LogOut, ChevronDown, Info } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { userService } from '../../../services/api/services/userService';

// Explicitly import DOM types to satisfy the linter
type HTMLDivElement = globalThis.HTMLDivElement;
type MouseEvent = globalThis.MouseEvent;
type Node = globalThis.Node;

export const UserProfile = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState<{
    role: string;
    subscription: string;
    subscriptionExpiryDate: string | null;
  } | null>(null);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch subscription info
  useEffect(() => {
    const fetchSubscriptionInfo = async () => {
      try {
        const response = await userService.getCurrentUserSubscription();
        if (response.success) {
          setSubscriptionInfo(response.data);
        }
      } catch (error) {
        console.error('Error fetching subscription info:', error);
      }
    };

    fetchSubscriptionInfo();
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  if (!user) {
    return null;
  }

  // Get subscription display info
  const getSubscriptionDisplayInfo = () => {
    if (!subscriptionInfo) {
      return { text: 'Cargando...', colorClass: 'text-gray-300' };
    }

    // If user is admin, show "Permanente"
    if (subscriptionInfo.role === 'Administrador') {
      return {
        text: 'Permanente',
        colorClass: 'text-orange-600',
      };
    }

    // If subscription is inactive, show "Desactivado"
    if (subscriptionInfo.subscription === 'inactive') {
      return {
        text: 'Desactivado',
        colorClass: 'text-gray-500',
      };
    }

    // If no expiry date, show dash
    if (!subscriptionInfo.subscriptionExpiryDate) {
      return {
        text: '-',
        colorClass: 'text-gray-500',
      };
    }

    const now = new Date();
    const expiry = new Date(subscriptionInfo.subscriptionExpiryDate);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffYears = diffDays / 365;

    // If more than 10 years, show "Permanente" in green
    if (diffYears > 10) {
      return {
        text: 'Permanente',
        colorClass: 'text-green-600',
      };
    }

    let colorClass = '';
    if (diffDays < 0) {
      // Expired
      colorClass = 'text-red-600';
    } else if (diffDays <= 14) {
      // Less than 2 weeks
      colorClass = 'text-red-600';
    } else if (diffDays <= 30) {
      // Less than 1 month
      colorClass = 'text-yellow-600';
    } else {
      // More than 1 month
      colorClass = 'text-green-600';
    }

    // Format date as YYYY/MM/DD for banner display
    const formattedDate = expiry.toLocaleDateString('en-CA'); // This gives YYYY-MM-DD format
    const displayDate = formattedDate.replace(/-/g, '/'); // Convert to YYYY/MM/DD

    return {
      text: displayDate,
      colorClass,
    };
  };

  const subscriptionDisplayInfo = getSubscriptionDisplayInfo();

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-brand-white hover:bg-brand-gray-dark rounded-md transition-colors duration-200"
      >
        <div className="flex items-center gap-2">
          {/* Subscription Expiry Banner */}
          <div className="flex items-center gap-1 px-2 py-1 bg-brand-white bg-opacity-15 rounded-md border border-brand-white border-opacity-20">
            <span className="text-xs text-brand-white font-medium">Expiración de suscripción:</span>
            <span
              className={`text-xs font-semibold ${
                subscriptionDisplayInfo.text === 'Permanente'
                  ? subscriptionDisplayInfo.colorClass === 'text-orange-600'
                    ? 'text-orange-300'
                    : 'text-green-300'
                  : subscriptionDisplayInfo.text === 'Cargando...'
                    ? 'text-gray-300'
                    : subscriptionDisplayInfo.colorClass === 'text-red-600'
                      ? 'text-red-300'
                      : subscriptionDisplayInfo.colorClass === 'text-yellow-600'
                        ? 'text-yellow-300'
                        : subscriptionDisplayInfo.colorClass === 'text-green-600'
                          ? 'text-green-300'
                          : 'text-brand-white'
              }`}
            >
              {subscriptionDisplayInfo.text}
            </span>
            <div className="group relative">
              <Info className="w-3 h-3 text-brand-white cursor-help opacity-70 hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 pointer-events-none">
                Contacte al administrador para más información
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>
          </div>

          <div className="w-8 h-8 bg-brand-gray-light rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-brand-white" />
          </div>
          <span className="hidden sm:block text-sm font-medium">
            {user.name} {user.surname}
          </span>
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-brand-white rounded-md shadow-lg py-1 z-50 border border-brand-gray-light">
          <div className="px-4 py-2 border-b border-brand-gray-light">
            <p className="text-sm font-medium text-text-primary">
              {user.name} {user.surname}
            </p>
            <p className="text-xs text-text-secondary">{user.username}</p>
            <p className="text-xs text-text-secondary capitalize">{user.role}</p>
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
          >
            <LogOut className="w-4 h-4" />
            {t('navbar.userProfile.signOut')}
          </button>
        </div>
      )}
    </div>
  );
};
